/**
 * WebSocket service for real-time communication
 * Automatically handles JWT authentication with authService integration
 */

import { authService } from './authService';

export interface WebSocketMessage {
  type: string;
  data?: any;
  payload?: any;
  timestamp?: number;
}

export interface WebSocketOptions {
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  maxReconnectInterval?: number;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private options: WebSocketOptions;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private listeners: Map<string, Set<(message: WebSocketMessage) => void>> = new Map();
  private token: string | null = null;
  private isAuthenticated = false;
  private authTokenRefreshListener: (() => void) | null = null;
  // Whether we have completed at least one successful open; used to tell a
  // genuine reconnect (fire a resync) from the very first connect.
  private hasConnectedBefore = false;
  private networkListenerCleanup: (() => void) | null = null;

  constructor(url: string, options: WebSocketOptions = {}) {
    this.url = url;
    this.options = {
      autoReconnect: true,
      // Base backoff; the real delay grows exponentially up to
      // maxReconnectInterval. Previously a flat 10s.
      reconnectInterval: 1000,
      // Never give up permanently. The old cap of 3 meant the socket died
      // after ~30s of failures (server restart, laptop asleep, network blip)
      // and only a full page reload could revive it — the root cause of the
      // "live views freeze, must refresh" symptom. Backoff (capped below)
      // keeps retries cheap while we keep trying indefinitely.
      maxReconnectAttempts: Number.MAX_SAFE_INTEGER,
      maxReconnectInterval: 30000,
      ...options,
    };

    // Initialize token from authService
    this.initializeTokenFromAuthService();

    // Set up listener for token refresh events
    this.setupAuthTokenRefreshListener();

    // Reconnect proactively when the network or tab visibility recovers, so a
    // backgrounded/asleep tab reconnects the instant it returns instead of
    // waiting for (or having exhausted) the backoff timer.
    this.setupNetworkListeners();
  }

  setToken(token: string): void {
    this.token = token;
    this.isAuthenticated = !!token;
  }
  
  clearToken(): void {
    this.token = null;
    this.isAuthenticated = false;
  }
  
  // Check if currently authenticated
  hasValidToken(): boolean {
    return this.isAuthenticated && !!this.token;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Ensure we have the latest token from authService
        this.syncTokenWithAuthService();
        
        // Emit connecting status
        this.emit('connection_status', { type: 'connection_status', data: 'connecting' });
        
        // Build WebSocket URL with token if available
        let wsUrl = this.url;
        if (this.token) {
          const separator = wsUrl.includes('?') ? '&' : '?';
          wsUrl = `${wsUrl}${separator}token=${this.token}`;
        }
        
        // Log connection attempt in development only
        if (import.meta.env.DEV) {
          console.log('WebSocket - Attempting to connect to:', wsUrl);
        }
        
        this.ws = new WebSocket(wsUrl);
        
        // Store authentication status
        this.isAuthenticated = !!this.token;

        this.ws.onopen = () => {
          if (import.meta.env.DEV) {
            console.log('WebSocket - Connection opened successfully');
          }
          this.reconnectAttempts = 0;
          
          // For authenticated connections, wait for connection confirmation
          if (this.isAuthenticated) {
            if (import.meta.env.DEV) {
              console.log('WebSocket - Waiting for authentication confirmation...');
            }
            // Set a timeout to resolve after authentication is confirmed
            setTimeout(() => {
              if (import.meta.env.DEV) {
                console.log('WebSocket - Authentication timeout, resolving anyway');
              }
              this.emitConnected();
              resolve();
            }, 3000);
          } else {
            // For anonymous connections, resolve immediately
            this.emitConnected();
            resolve();
          }
        };

        this.ws.onmessage = (event) => {
          if (import.meta.env.DEV) {
            console.log('WebSocket - Message received:', event.data);
          }
          try {
            const raw = JSON.parse(event.data);
            // Normalize: backend sends both `data` and `payload` from different places
            const message: WebSocketMessage = {
              ...raw,
              data: raw.data ?? raw.payload,
              payload: raw.payload ?? raw.data,
            };
            if (import.meta.env.DEV) {
              console.log('WebSocket - Parsed message:', message);
            }
            
            // Handle connection confirmation for authenticated connections
            if (message.type === 'connection_confirmed' && this.isAuthenticated) {
              if (import.meta.env.DEV) {
                console.log('WebSocket - Connection confirmed, authentication successful');
              }
              this.emitConnected();
            }
            
            this.emit(message.type, message);
          } catch (error) {
            console.error('Failed to parse message:', error);
            if (import.meta.env.DEV) {
              console.error('Raw message data:', event.data);
            }
          }
        };

        this.ws.onclose = (event) => {
          if (import.meta.env.DEV) {
            console.log('WebSocket - Connection closed');
            console.log('Close code:', event.code);
            console.log('Close reason:', event.reason);
            console.log('Was clean:', event.wasClean);
          }
          // Emit disconnected status
          this.emit('connection_status', { type: 'connection_status', data: 'disconnected' });
          if (this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts!) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket - Error occurred:', error);
          if (import.meta.env.DEV) {
            console.error('Connection state:', this.ws?.readyState);
          }
          // Emit error status
          this.emit('connection_status', { type: 'connection_status', data: 'error' });
          reject(error);
        };
      } catch (error) {
        // Emit error status
        this.emit('connection_status', { type: 'connection_status', data: 'error' });
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Emit disconnected status before closing
      this.emit('connection_status', { type: 'connection_status', data: 'disconnected' });
      this.ws.close();
      this.ws = null;
    }
  }
  
  // Initialize token from authService
  private initializeTokenFromAuthService(): void {
    const token = authService.getAccessToken();
    if (token) {
      this.setToken(token);
      if (import.meta.env.DEV) {
        console.log('WebSocket - Initialized with token from authService');
      }
    } else {
      if (import.meta.env.DEV) {
        console.log('WebSocket - No token available from authService');
      }
    }
  }
  
  // Sync token with authService (get latest token)
  private syncTokenWithAuthService(): void {
    const authToken = authService.getAccessToken();
    
    // Update token if it has changed or if we don't have one
    if (authToken !== this.token) {
      if (authToken) {
        this.setToken(authToken);
        if (import.meta.env.DEV) {
          console.log('WebSocket - Token updated from authService');
        }
      } else {
        this.clearToken();
        if (import.meta.env.DEV) {
          console.log('WebSocket - Token cleared (no auth token available)');
        }
      }
    }
  }
  
  // Set up listener for token refresh events
  private setupAuthTokenRefreshListener(): void {
    // Listen for tokensRefreshed events
    const handleTokensRefreshed = () => {
      if (import.meta.env.DEV) {
        console.log('WebSocket - Tokens refreshed, updating WebSocket token');
      }
      this.syncTokenWithAuthService();
      
      // If we're connected, reconnect to use the new token
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        if (import.meta.env.DEV) {
          console.log('WebSocket - Reconnecting with new token...');
        }
        this.reconnectWithNewToken();
      }
    };
    
    authService.addEventListener('tokensRefreshed', handleTokensRefreshed);
    this.authTokenRefreshListener = () => {
      authService.removeEventListener('tokensRefreshed', handleTokensRefreshed);
    };
  }
  
  // Reconnect with new token
  private reconnectWithNewToken(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Wait long enough for server to process close frame before reconnecting
    setTimeout(() => {
      this.connect().catch(error => {
        console.error('Failed to reconnect with new token:', error);
      });
    }, 1000);
  }
  
  // Cleanup method
  destroy(): void {
    if (this.authTokenRefreshListener) {
      this.authTokenRefreshListener();
      this.authTokenRefreshListener = null;
    }
    if (this.networkListenerCleanup) {
      this.networkListenerCleanup();
      this.networkListenerCleanup = null;
    }
    this.disconnect();
  }

  send(type: string, data?: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = {
        type,
        payload: data,
        timestamp: Date.now(),
      };
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  on(eventType: string, listener: (message: WebSocketMessage) => void): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);
  }

  off(eventType: string, listener: (message: WebSocketMessage) => void): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  private emit(eventType: string, message: WebSocketMessage): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          console.error(`Error in WebSocket listener for ${eventType}:`, error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    // Never stack timers — a single pending reconnect is enough.
    if (this.reconnectTimer) return;

    const base = this.options.reconnectInterval ?? 1000;
    const cap = this.options.maxReconnectInterval ?? 30000;
    // Exponential backoff capped at `cap`, with up to 30% jitter to avoid a
    // thundering herd when the server comes back and every client reconnects.
    const backoff = Math.min(cap, base * Math.pow(2, this.reconnectAttempts));
    const delay = Math.round(backoff + Math.random() * 0.3 * backoff);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      if (import.meta.env.DEV) {
        console.log(`WebSocket - reconnect attempt ${this.reconnectAttempts} (waited ${delay}ms)`);
      }
      this.connect().catch(() => {
        // onclose fires and reschedules with the next backoff step.
      });
    }, delay);
  }

  // Emit 'connected' and, when this is a genuine reconnect (not the first
  // connect of the session), a 'ws_reconnected' signal so consumers can
  // re-hydrate state that may have gone stale while the socket was down.
  private emitConnected(): void {
    const isReconnect = this.hasConnectedBefore;
    this.hasConnectedBefore = true;
    this.emit('connection_status', { type: 'connection_status', data: 'connected' });
    if (isReconnect) {
      this.emit('ws_reconnected', { type: 'ws_reconnected' });
    }
  }

  // Reconnect proactively when the browser tells us the network or the tab is
  // back. A backgrounded tab throttles timers, so the backoff loop may be
  // asleep; these events wake it immediately.
  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') return;

    const onOnline = () => {
      if (import.meta.env.DEV) console.log('WebSocket - network online, reconnecting');
      this.forceReconnect();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !this.isConnected()) {
        if (import.meta.env.DEV) console.log('WebSocket - tab visible, reconnecting');
        this.forceReconnect();
      }
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    this.networkListenerCleanup = () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }

  // Reset backoff and attempt an immediate reconnect, unless already connected
  // or an attempt is already in flight/scheduled.
  private forceReconnect(): void {
    if (this.isConnected()) return;
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.connect().catch(() => {
      // onclose reschedules with backoff.
    });
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Method to match the useWebSocket hook expectations
  onMessage(eventType: string, listener: (message: WebSocketMessage) => void): (() => void) {
    this.on(eventType, listener);
    // Return cleanup function
    return () => this.off(eventType, listener);
  }

  // Method to handle connection status changes
  onConnectionStatus(listener: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void): (() => void) {
    // Store the listener and call it when connection status changes
    const statusListener = (message: WebSocketMessage) => {
      if (message.type === 'connection_status') {
        listener(message.data as 'connecting' | 'connected' | 'disconnected' | 'error');
      }
    };
    
    this.on('connection_status', statusListener);
    
    // Return cleanup function
    return () => this.off('connection_status', statusListener);
  }

  // Method to get connection state (used by useWebSocket hook)
  getConnectionState(): 'connecting' | 'connected' | 'disconnected' | 'error' {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'error';
    }
  }

  // Method to send ping (used by useWebSocket hook)
  sendPing(): void {
    this.send('ping');
  }

  // Method to send messages with structured format (used by various components)
  sendMessage(message: { type: string; payload?: any }): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send(message.type, message.payload);
      return true;
    }
    return false;
  }

  // Method to check if connection is recoverable (used by cleanup tests)
  isRecoverable(): boolean {
    return (this.options.autoReconnect ?? true) &&
           this.reconnectAttempts < (this.options.maxReconnectAttempts || 5);
  }
  
  // Method to manually refresh token (useful for testing)
  refreshToken(): void {
    if (import.meta.env.DEV) {
      console.log('WebSocket - Manually refreshing token...');
    }
    this.syncTokenWithAuthService();
    
    // Reconnect if currently connected
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.reconnectWithNewToken();
    }
  }
  
  // Get current token status for debugging
  getTokenStatus(): { hasToken: boolean; tokenLength: number; isAuthenticated: boolean } {
    return {
      hasToken: !!this.token,
      tokenLength: this.token ? this.token.length : 0,
      isAuthenticated: this.isAuthenticated
    };
  }
}

// Create singleton instance for unified WebSocket
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws/ws';
if (import.meta.env.DEV) {
  console.log('WebSocket Service - Initializing with URL:', WS_URL);
}
export const websocketService = new WebSocketService(WS_URL);

export default websocketService;